import { useMemo } from "react";
import type { BaseEntity } from "../../../UniversalCRMTable/types";
import type { UniversalEntityPageContextValue } from "../../contexts/UniversalEntityPageContext";
import { useCompositionContextParts } from "./useCompositionContextParts";
import type { CompositionContextValueParams } from "./compositionContextValueTypes";

export type {
  CompositionNavigation,
  CompositionDataStateInput,
  CompositionHandlersInput,
  CompositionContextValueParams,
} from "./compositionContextValueTypes";

/**
 * Assembles and memoizes the final context value.
 * Combines all state and handlers into a single context object.
 *
 * Stable sub-memos (dataStateWithoutLoading, stable noops, convertToGraphQL)
 * are extracted into useCompositionContextParts to keep this file under 300 lines.
 */
export function useCompositionContextValue<T extends BaseEntity>(
  params: CompositionContextValueParams<T>
): UniversalEntityPageContextValue<T> {
  const {
    dataStateWithoutLoading,
    stableSetSerializeFilters,
    stableSetApplyFilters,
    convertToGraphQLCallback,
  } = useCompositionContextParts({
    dataState: params.dataState,
    config: params.config,
    entityName: params.entityName,
  });

  // Memoize the entire context value to prevent child component remounts.
  // Only recalculate when actual data changes, not on every parent render.
  // This is critical for preventing full page remounts during search/filter updates.
  // NOTE: isLoading is NOT included in the dependency array — it's added separately.
  const contextValue = useMemo(
    () => ({
      pageConfig: {
        entityName: params.entityName,
        config: params.config,
        hooks: params.hooks,
        entityFormProps: params.entityFormProps,
        customComponents: params.customComponents,
      },
      auth: {
        appUser: params.appUser,
        isAuthenticated: params.isAuthenticated,
        isClientUser: params.isClientUser,
        authLoading: params.authLoading,
        isEmployee: params.isEmployee,
        isAdmin: params.isAdmin,
        isWTCCRM: params.isWTCCRM,
        isWTCEmployee: params.isWTCEmployee,
        isAdminRole: params.isAdminRole,
        isMobile: params.isMobile,
        canCreate: params.canCreate,
      },
      navigation: params.navigation,
      dataState: {
        ...dataStateWithoutLoading,
        isLoading: params.dataState.isLoading,
      },
      entityState: {
        editingItem: params.entityState.editingItem,
        setEditingItem: params.entityState.setEditingItem,
        deletingItem: params.entityState.deletingItem,
        setDeletingItem: params.entityState.setDeletingItem,
        showComposeModal: params.entityState.showComposeModal,
        setShowComposeModal: params.entityState.setShowComposeModal,
        showExportModal: params.entityState.showExportModal,
        setShowExportModal: params.entityState.setShowExportModal,
        bulkEmailExportData: params.entityState.bulkEmailExportData,
        setBulkEmailExportData: params.entityState.setBulkEmailExportData,
      },
      filterState: {
        searchTerm: params.filterState.searchTerm,
        setSearchTerm: params.filterState.setSearchTerm,
        debouncedSearchTerm: params.filterState.debouncedSearchTerm,
        tableFilters: params.filterState.tableFilters,
        setTableFilters: params.filterState.setTableFilters,
        currentFilters: params.normalizedCurrentFilters,
        setCurrentFilters: params.filterState.setCurrentFilters,
        advancedFilters: params.normalizedCurrentFilters?.advancedFilters ?? {},
        setAdvancedFilters: params.filterState.setAdvancedFilters,
        filterOperator: params.filterState.filterOperator,
        setFilterOperator: params.filterState.setFilterOperator,
        activeViewId: params.navigation.activeViewId,
        setActiveViewId: params.filterState.setActiveViewId,
        applySavedView: params.filterState.applySavedView,
        serializeFilters: params.filterState.serializeFilters,
        setSerializeFilters: stableSetSerializeFilters,
        applyFilters: params.filterState.applyFilters,
        setApplyFilters: stableSetApplyFilters,
        convertToGraphQL: convertToGraphQLCallback,
      },
      exportState: {
        isExporting: params.entityState.isExporting,
        setIsExporting: params.entityState.setIsExporting,
        exportProgress: params.entityState.exportProgress,
        setExportProgress: params.entityState.setExportProgress,
        showExportModal: params.entityState.showExportModal,
        setShowExportModal: params.entityState.setShowExportModal,
      },
      handlers: {
        handleCreateItem: params.handlers.handleCreateItem,
        handleEditItem: params.handlers.handleEditItem,
        handleInlineEdit: params.handlers.handleInlineEdit,
        handleDeleteItem: params.handlers.handleDeleteItem,
        handleViewItem: params.handlers.handleViewItem,
        handleItemCreated: params.handlers.handleItemCreated,
        handleItemUpdated: params.handlers.handleItemUpdated,
        handleItemDeleted: params.handlers.handleItemDeleted,
        handleImportItems: params.handlers.handleImportItems,
        handleCancelDelete: params.handlers.handleCancelDelete,
        handleExportWithScope: params.handlers.handleExportWithScope,
        handleExportItems: params.handlers.handleExportItems,
        handleSavedViewsReady: params.handlers.handleSavedViewsReady,
        handleComposeMessage: params.handlers.handleComposeMessage,
        handleOpenDetailModal: params.handlers.handleOpenDetailModal,
      },
      callbacks: {
        handleGroupingExport: params.callbacks.handleGroupingExport,
        renderSimpleTable: params.callbacks.renderSimpleTable,
      },
      mutations: {
        deleteMutation: params.mutations.deleteMutation,
      },
      bulkActions: params.bulkActions,
      bulkActionMutations: params.bulkActionMutations,
      savedViewsState: params.savedViewsState,
      viewModeState: {
        viewMode: params.viewMode,
        setViewMode: params.setViewMode,
      },
      infiniteScrollState: {
        infiniteScrollEnabled: params.paginationModeState.paginationMode.mode === "infinite-scroll",
        setInfiniteScrollEnabled: params.paginationModeState.toggleInfiniteScroll,
      },
      paginationModeState: params.paginationModeState,
    }),
    [
      params.entityName,
      params.config,
      params.hooks,
      params.entityFormProps,
      params.customComponents,
      params.appUser,
      params.isAuthenticated,
      params.isClientUser,
      params.authLoading,
      params.isEmployee,
      params.isAdmin,
      params.isWTCCRM,
      params.isWTCEmployee,
      params.isAdminRole,
      params.isMobile,
      params.canCreate,
      // Use individual stable props instead of the whole navigation object.
      params.navigation.router,
      params.navigation.searchParams,
      params.navigation.apolloClient,
      params.navigation.activeViewId,
      dataStateWithoutLoading,
      params.entityState.editingItem,
      params.entityState.setEditingItem,
      params.entityState.deletingItem,
      params.entityState.setDeletingItem,
      params.entityState.showComposeModal,
      params.entityState.setShowComposeModal,
      params.entityState.showExportModal,
      params.entityState.setShowExportModal,
      params.entityState.bulkEmailExportData,
      params.entityState.setBulkEmailExportData,
      params.filterState.searchTerm,
      params.filterState.setSearchTerm,
      params.filterState.debouncedSearchTerm,
      params.filterState.tableFilters,
      params.filterState.setTableFilters,
      params.filterState.currentFilters,
      params.filterState.setCurrentFilters,
      params.filterState.advancedFilters,
      params.filterState.setAdvancedFilters,
      params.filterState.filterOperator,
      params.filterState.setFilterOperator,
      params.filterState.setActiveViewId,
      params.filterState.applySavedView,
      params.filterState.serializeFilters,
      params.filterState.applyFilters,
      convertToGraphQLCallback,
      params.entityState.isExporting,
      params.entityState.setIsExporting,
      params.entityState.exportProgress,
      params.entityState.setExportProgress,
      params.handlers.handleCreateItem,
      params.handlers.handleEditItem,
      params.handlers.handleInlineEdit,
      params.handlers.handleDeleteItem,
      params.handlers.handleViewItem,
      params.handlers.handleItemCreated,
      params.handlers.handleItemUpdated,
      params.handlers.handleItemDeleted,
      params.handlers.handleImportItems,
      params.handlers.handleCancelDelete,
      params.handlers.handleExportWithScope,
      params.handlers.handleExportItems,
      params.handlers.handleSavedViewsReady,
      params.handlers.handleComposeMessage,
      params.handlers.handleOpenDetailModal,
      params.callbacks.handleGroupingExport,
      params.callbacks.renderSimpleTable,
      params.mutations.deleteMutation,
      params.bulkActions,
      params.bulkActionMutations,
      params.savedViewsState,
      params.normalizedCurrentFilters,
      params.viewMode,
      params.setViewMode,
      // Use primitive/stable deps from paginationModeState instead of the whole object.
      params.paginationModeState.paginationMode.mode,
      params.paginationModeState.paginationMode.cursor,
      params.paginationModeState.paginationMode.page,
      params.paginationModeState.paginationMode.pageSize,
      params.paginationModeState.setPage,
      params.paginationModeState.setPageSize,
      params.paginationModeState.setCursor,
      params.paginationModeState.toggleInfiniteScroll,
      stableSetSerializeFilters,
      stableSetApplyFilters,
    ]
  );

  return contextValue;
}
